import { FocusSelectionParams } from '../../coaching-base/config';
import {
    CoachableMetric,
    CoachingType,
    CommitmentFocusSummaryDto,
    FocusDto,
    Opportunity,
} from '../../coaching-base/general';
import { sortOpportunities } from '../../coaching-base/opportunity';
import {
    CellNode,
    RowNode,
    Sort,
    SortOrder,
} from '../../coaching-observation/experimental.grid';
import { BehaviourItem } from '../models/behaviour.coaching.model';
import { SubBehaviourItem } from '../models/subbehaviour.coaching.model';
import { TagItem } from '../models/tag.coaching.model';

/** used for passing focus info internally */
export interface FocusShallow {
    behaviourId?: number;
    subBehaviourId?: number;
}

/// //

export enum ColumnID {
    METRIC = 'metric',
    OA = 'oa',
    ON_SCORECARD = 'on_scorecard',

    /** hidden column for sub-sorting by opportunity rules */
    OA_OPPORTUNITY = 'oa_opportunity',
}

export enum TemplateID {
    HEADER = 'header',
    CONTENT = 'content',
}

export interface RowContext {
    templateId: TemplateID;
}

export interface CellContext {
    templateId: TemplateID;
    metric?: CoachableMetric;
    checked?: boolean;
}

export const MIN_FILTER_TEXT_LENGTH = 3;

export enum TagSetMode {
    DEFAULT,
    FOCUS_ONLY,
    METRIC_ONLY,
}

export interface TagSelection {
    behaviour: BehaviourItem;
    subBehaviour: SubBehaviourItem;
    metrics: Map<number, CoachableMetric>;
}

export interface TagItemCategories {
    active: {
        isCollapsed?: boolean;
        tagItems?: TagItem[];
    };
    closed: {
        isCollapsed?: boolean;
        tagItems?: TagItem[];
    };
    team: {
        isCollapsed?: boolean;
        tagItems?: TagItem[];
    };
}

export interface TagItemCategoryView {
    title?: string;
    isCollapsed?: boolean;
    tagItems?: TagItem[];
}

export function emptySelection(): TagSelection {
    return {
        behaviour: null,
        subBehaviour: null,
        metrics: new Map<number, CoachableMetric>(),
    };
}

const coachingTypeWeightMap = {
    [CoachingType.UNCOACHED]: 1,
    [CoachingType.COACHED]: 2,
    [CoachingType.NON_OPPORTUNITY]: 3,
    // others would have been filtered out
};

export function compare(
    cellA: CellNode,
    cellB: CellNode,
    rowA: RowNode,
    rowB: RowNode,
    def: Sort,
): number {
    try {
        const mode: 'number' | 'string' =
            def.columnId === ColumnID.METRIC ? 'string' : 'number';
        const ctxA: CellContext = cellA.context.ctx;
        const ctxB: CellContext = cellB.context.ctx;
        let valA: any;
        let valB: any;
        switch (def.columnId) {
            case ColumnID.METRIC:
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA = ctxA.metric && ctxA.metric.metricName;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB = ctxB.metric && ctxB.metric.metricName;
                break;
            case ColumnID.OA:
                // eslint-disable-next-line no-case-declarations
                const delta = def.order === SortOrder.ASC ? 1 : -1;
                valA =
                    ctxA.metric.coachTypeId == null
                        ? delta * 999
                        : coachingTypeWeightMap[ctxA.metric.coachTypeId];
                valB =
                    ctxB.metric.coachTypeId == null
                        ? delta * 999
                        : coachingTypeWeightMap[ctxB.metric.coachTypeId];
                break;
            case ColumnID.ON_SCORECARD:
                valA = ctxA.metric.isInScorecard ? 1 : 999;
                valB = ctxB.metric.isInScorecard ? 1 : 999;
                break;
            case ColumnID.OA_OPPORTUNITY:
                // TODO revise casting. use of sortOpportunities
                // eslint-disable-next-line no-case-declarations, @typescript-eslint/consistent-type-assertions
                const makeOpp = (ctx: CellContext): Opportunity =>
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    ({
                        metricShortName: ctx.metric.metricShortName,
                        rankScore: ctx.metric.rankScore,
                        percentToTarget: ctx.metric.percentToTarget,
                        coachTypeId: ctx.metric.coachTypeId,
                        isOppActive: ctx.metric.isOppActive,
                    }) as Opportunity;
                return sortOpportunities(
                    makeOpp(ctxA),
                    makeOpp(ctxB),
                    def.order === SortOrder.ASC ? 'asc' : 'desc',
                );
            default:
                console.error('invalid column');
                return Number.MIN_SAFE_INTEGER;
        }
        if (mode === 'number') {
            valA = valA == null ? Number.MIN_SAFE_INTEGER : valA;
            valB = valB == null ? Number.MIN_SAFE_INTEGER : valB;
        } else {
            valA = valA == null ? '' : valA;
            valB = valB == null ? '' : valB;
        }
        return mode === 'number'
            ? def.order === SortOrder.ASC
                ? valA - valB
                : valB - valA
            : def.order === SortOrder.ASC
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
    } catch (e) {
        console.log(e);
        return Number.MIN_SAFE_INTEGER;
    }
}

/** {behaviour}-{sub-behaviour} */
export type FocusIdentifier = string;

export const composeTagIdentifierNext = <T extends FocusShallow>(
    tag: T,
): FocusIdentifier =>
    !tag
        ? 'undefined-undefined'
        : `${tag.behaviourId == null ? 'undefined' : tag.behaviourId}-${
              tag.subBehaviourId == null ? 'undefined' : tag.subBehaviourId
          }`;

/** {behaviour}-{sub-behaviour}-{status} */
export type FocusStatusIdentifier = string;

export const composeTagStatusIdentifier = <T extends CommitmentFocusSummaryDto>(
    tag: T,
): FocusStatusIdentifier => {
    if (!tag) {
        return 'undefined-undefined';
    }
    return `${tag.behaviourId == null ? 'undefined' : tag.behaviourId}-
    ${tag.subBehaviourId == null ? 'undefined' : tag.subBehaviourId}-
    ${tag.statusId == null ? 'undefined' : tag.statusId}`;
};

export function reconstructTagFromIdentifierNext(
    identifier: string,
): FocusShallow {
    const result = {};
    if (!identifier) {
        return result;
    }
    const parts = identifier.split('-');
    if (parts.length !== 2) {
        return result;
    }
    const cleaned = parts
        .map((e) => parseInt(e))
        .map((e) => (!isNaN(e) ? e : null));
    return {
        behaviourId: cleaned[0],
        subBehaviourId: cleaned[1],
    };
}

export interface TagsDialogData {
    storePathPrefix: string;
    mode: TagSetMode;
    focusSelectionParams: FocusSelectionParams;
    commitmentId: number;
    performerId: number;
    selected: FocusDto;
    pinnedMetrics: number[];
    activeMetricId?: number;
    canSelectFocus?: (focus: FocusDto) => boolean | Promise<boolean>;
}
